import { useEffect } from "react";
import { useState } from "react";
import { DateRange, YearGroup } from "widgets/events/types";
import { MultilingualLangKey } from "shared/types";
import { i18nLanguage } from "i18n";
import { es, fr } from "date-fns/locale";
import { format, lastDayOfMonth, parse } from "date-fns";
import { EVENT_DATE_FORMAT } from "shared/utils";

type UseMonthYearSelectorProps = {
  yearGroups: YearGroup[];
  firstMonthOfYearWithEvents: { [key: string]: string };
  dateRange: DateRange;
  lang: MultilingualLangKey;
  onDateRangeChange: (dateRange: DateRange) => void;
};

export const useMonthYearSelector = ({
  yearGroups,
  firstMonthOfYearWithEvents,
  dateRange,
  lang,
  onDateRangeChange,
}: UseMonthYearSelectorProps) => {
  const [title, setTitle] = useState("");
  // When the dateRange changes, the title needs to be updated
  // If the dateRange spans an entire month, the month selector (desktop or mobile) is visible
  // If the dateRange does NOT span an entire month, the subset of dates are shown
  useEffect(() => {
    const now = new Date();
    const start = parse(dateRange.from, EVENT_DATE_FORMAT, now);
    const end = parse(dateRange.to, EVENT_DATE_FORMAT, now);
    const locale =
      lang === i18nLanguage.en ? undefined : lang === i18nLanguage.es ? es : fr;

    const isDifferentMonth =
      dateRange.from.substring(5, 7) !== dateRange.to.substring(5, 7);
    const isDifferentYear =
      dateRange.from.substring(0, 5) !== dateRange.to.substring(0, 5);

    if (!dateRange.isFullMonth) {
      if (isDifferentYear) {
        setTitle(
          `${format(start, "MMM d yyyy", { locale })}-${format(end, "MMM d yyyy", { locale })}`,
        );
      } else if (isDifferentMonth) {
        setTitle(
          `${format(start, "MMM d", { locale })}-${format(end, "MMM d", { locale })}, ${start.getFullYear()}`,
        );
      } else {
        setTitle(
          `${format(start, "MMM d", { locale })}-${end.getDate()}, ${start.getFullYear()}`,
        );
      }
    }
  }, [dateRange, lang]);

  /**
   * Keep track of the selected year and month
   * - This is used to prevent unnecessary re-renders
   */
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  useEffect(() => {
    if (dateRange && yearGroups.length > 0) {
      setSelectedYear(dateRange.from.substring(0, 4));
      setSelectedMonth(dateRange.from);
    }
  }, [dateRange, yearGroups]);

  /**
   * Handle clicking on a year
   * - If the year is already selected, do nothing
   * - Otherwise, set the selected year and month to the first month of the year
   * @param year - The year to select
   */
  const handleYearClick = (year: string) => {
    if (year === selectedYear) {
      return;
    }
    setSelectedYear(year);
    const firstMonthWithEvents = firstMonthOfYearWithEvents[year];
    if (firstMonthWithEvents) {
      handleMonthClick(firstMonthWithEvents);
    }
  };

  /**
   * Handle clicking on a month
   * - If the month is already selected, do nothing
   * - Otherwise, set the selected month and update the date range
   * @param month - The month to select
   */
  const handleMonthClick = (month: string) => {
    if (month === selectedMonth) {
      return;
    }

    const newDateRange: DateRange = {
      from: month,
      to: format(
        lastDayOfMonth(parse(month, EVENT_DATE_FORMAT, new Date())),
        EVENT_DATE_FORMAT,
      ),
      isFullMonth: true,
    };
    onDateRangeChange(newDateRange);
    setSelectedMonth(month);
  };

  return {
    title,
    selectedYear,
    selectedMonth,
    handleYearClick,
    handleMonthClick,
    setSelectedYear,
  };
};
